const PLAIN_DECIMAL = /^([+-]?)(\d*)(?:\.(\d*))?$/;

/** Add one to a string of decimal digits (`'099'` → `'100'`, `'99'` → `'100'`). */
function incrementDigits(digits: string): string {
  const out = digits.split('');
  for (let i = out.length - 1; i >= 0; i--) {
    if (out[i] !== '9') {
      out[i] = String(Number(out[i]) + 1);
      return out.join('');
    }
    out[i] = '0';
  }
  return `1${out.join('')}`;
}

/**
 * Commercial rounding of an amount to `fractionDigits` digits: half away from
 * zero (`1.005` → `1.01`, `2.675` → `2.68`, `-1.005` → `-1.01`).
 *
 * Rounds on the decimal digits themselves, never on a binary float —
 * `(1.005).toFixed(2)` is `"1.00"`. Accepts `.` or `,` as the decimal mark and
 * returns a canonical `.` string with exactly `fractionDigits` digits after the
 * mark (`'12'` → `'12.00'`), or `null` when the text holds no number. Text a
 * plain decimal cannot express (`'12abc'`, `'1e-7'`) goes through
 * `Number.parseFloat` first, as the display always did.
 */
export function roundCurrencyAmount(value: string, fractionDigits = 2): string | null {
  let normalized = String(value ?? '')
    .trim()
    .replace(',', '.');
  let match = PLAIN_DECIMAL.exec(normalized);
  if (!match || !/\d/.test(normalized)) {
    const n = Number.parseFloat(normalized);
    if (!Number.isFinite(n)) return null;
    normalized = String(n);
    match = PLAIN_DECIMAL.exec(normalized);
    // Exponent notation outside toFixed's plain range: keep the float path.
    if (!match) return n.toFixed(fractionDigits);
  }

  const [, sign, integerDigits, fractionText = ''] = match;
  const padded = fractionText.padEnd(fractionDigits + 1, '0');
  let kept = (integerDigits || '0') + padded.slice(0, fractionDigits);
  if (padded.charCodeAt(fractionDigits) >= '5'.charCodeAt(0)) kept = incrementDigits(kept);

  const integerPart = kept.slice(0, kept.length - fractionDigits).replace(/^0+(?=\d)/, '') || '0';
  const fractionPart = kept.slice(kept.length - fractionDigits);
  const isZero = /^0*$/.test(integerPart + fractionPart);
  const result = fractionDigits > 0 ? `${integerPart}.${fractionPart}` : integerPart;
  return sign === '-' && !isZero ? `-${result}` : result;
}

/**
 * Display/edit formatting for amount fields: always two fraction digits,
 * commercially rounded (see `roundCurrencyAmount`), German grouping
 * (1.234,56). Idle mode adds thousand separators; the focused buffer does not
 * (easier to edit).
 */
export function formatCurrencyAmount(value: string, thousandSteps = true): string {
  const rounded = roundCurrencyAmount(value);
  if (rounded === null) return thousandSteps ? '0,00' : '0';

  const [integer, decimal] = rounded.split('.');
  const sign = integer.startsWith('-') ? '-' : '';
  const digits = integer.replace('-', '');
  if (!thousandSteps) return `${sign}${digits},${decimal}`;

  let formatted = '';
  let count = 0;
  for (let i = digits.length - 1; i >= 0; i--) {
    if (count > 0 && count % 3 === 0) formatted = '.' + formatted;
    formatted = digits[i] + formatted;
    count++;
  }
  return `${sign}${formatted},${decimal}`;
}
